import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { 
  RefreshCw, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  AlertTriangle,
  RotateCcw,
  CreditCard,
  User,
  ArrowRight,
  Loader2,
  DollarSign,
  Calendar,
  Info,
  Copy,
  ExternalLink,
  HandCoins,
  ClipboardCheck
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

interface RefundVoucher {
  id: string;
  voucher_number: string;
  total_amount: number;
  refund_amount: number | null;
  status: string;
  razorpay_payment_id: string | null;
  razorpay_refund_id: string | null;
  refund_method: string | null;
  refund_error: string | null;
  refund_attempts: number | null;
  refund_initiated_at: string | null;
  refund_completed_at: string | null;
  is_organizer: boolean | null;
  participant: {
    name: string | null;
    email: string;
  };
  request: {
    id: string;
    product_name: string;
    customer_email: string;
  };
}

interface RefundManagementProps {
  merchantId: string;
  currency: string;
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  INR: '₹',
  USD: '$',
  EUR: '€',
  GBP: '£',
};

export default function RefundManagement({ merchantId, currency }: RefundManagementProps) {
  const [vouchers, setVouchers] = useState<RefundVoucher[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<string | null>(null);
  const [processingAll, setProcessingAll] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    voucher?: RefundVoucher;
    action: 'process' | 'manual' | 'retry';
  }>({ open: false, action: 'process' });
  const [manualWorkflowDialog, setManualWorkflowDialog] = useState<{
    open: boolean;
    voucher?: RefundVoucher;
    step: number;
    checklist: boolean[];
    notes: string;
    feeAcknowledged: boolean;
  }>({ open: false, step: 1, checklist: [false, false, false, false], notes: "", feeAcknowledged: false });

  const currencySymbol = CURRENCY_SYMBOLS[currency] || currency;

  const fetchVouchers = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('gift_vouchers')
        .select(`
          id,
          voucher_number,
          total_amount,
          refund_amount,
          status,
          razorpay_payment_id,
          razorpay_refund_id,
          refund_method,
          refund_error,
          refund_attempts,
          refund_initiated_at,
          refund_completed_at,
          is_organizer,
          split_payment_participants!inner(name, email),
          split_payment_requests!inner(id, product_name, customer_email, merchant_id)
        `)
        .eq('split_payment_requests.merchant_id', merchantId)
        .in('status', ['pending_refund', 'refunding', 'refund_failed', 'refunded'])
        .order('created_at', { ascending: false });

      if (error) throw error;

      const formatted = (data || []).map((v: any) => ({
        id: v.id,
        voucher_number: v.voucher_number,
        total_amount: v.total_amount,
        refund_amount: v.refund_amount,
        status: v.status,
        razorpay_payment_id: v.razorpay_payment_id,
        razorpay_refund_id: v.razorpay_refund_id,
        refund_method: v.refund_method,
        refund_error: v.refund_error,
        refund_attempts: v.refund_attempts,
        refund_initiated_at: v.refund_initiated_at,
        refund_completed_at: v.refund_completed_at,
        is_organizer: v.is_organizer,
        participant: {
          name: v.split_payment_participants?.name,
          email: v.split_payment_participants?.email,
        },
        request: {
          id: v.split_payment_requests?.id,
          product_name: v.split_payment_requests?.product_name,
          customer_email: v.split_payment_requests?.customer_email,
        },
      }));

      setVouchers(formatted);
    } catch (error: any) {
      console.error('Error fetching refund vouchers:', error);
      toast.error('Failed to load refund data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVouchers();
  }, [merchantId]);

  const processRefund = async (voucherId: string, action: 'process_single' | 'mark_manual' | 'retry_failed') => {
    setProcessing(voucherId);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/process-refunds`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            voucher_id: voucherId,
            action: action === 'retry_failed' ? 'process_single' : action,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to process refund');
      }

      if (action === 'mark_manual') {
        toast.success('Refund marked as complete');
      } else if (result.results?.[0]?.success) {
        toast.success('Refund processed successfully via Razorpay');
      } else {
        toast.warning(result.results?.[0]?.error || 'Refund requires manual processing');
      }

      fetchVouchers();
    } catch (error: any) {
      console.error('Error processing refund:', error);
      toast.error(error.message || 'Failed to process refund');
    } finally {
      setProcessing(null);
      setConfirmDialog({ open: false, action: 'process' });
    }
  };

  const processAllPending = async () => {
    const pendingVouchers = vouchers.filter(v => v.status === 'pending_refund');
    if (pendingVouchers.length === 0) {
      toast.info('No pending refunds to process');
      return;
    }

    setProcessingAll(true);
    try {
      // Group by request_id
      const requestIds = [...new Set(pendingVouchers.map(v => v.request.id))];

      for (const requestId of requestIds) {
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/process-refunds`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              request_id: requestId,
              action: 'process_all',
            }),
          }
        );

        const result = await response.json();
        console.log('Batch refund result:', result);
      }

      toast.success('All pending refunds are being processed');
      fetchVouchers();
    } catch (error: any) {
      console.error('Error processing all refunds:', error);
      toast.error('Failed to process some refunds');
    } finally {
      setProcessingAll(false);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const openManualWorkflow = (voucher: RefundVoucher) => {
    setManualWorkflowDialog({
      open: true,
      voucher,
      step: 1,
      checklist: [false, false, false, false],
      notes: "",
      feeAcknowledged: false,
    });
  };

  const handleManualWorkflowComplete = async () => {
    if (!manualWorkflowDialog.voucher) return;
    
    setProcessing(manualWorkflowDialog.voucher.id);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/process-refunds`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            voucher_id: manualWorkflowDialog.voucher.id,
            action: 'mark_manual',
            notes: manualWorkflowDialog.notes,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to complete refund');
      }

      toast.success('Refund marked as complete. Participant will be notified.');
      setManualWorkflowDialog({ open: false, step: 1, checklist: [false, false, false, false], notes: "", feeAcknowledged: false });
      fetchVouchers();
    } catch (error: any) {
      console.error('Error completing manual refund:', error);
      toast.error(error.message || 'Failed to complete refund');
    } finally {
      setProcessing(null);
    }
  };

  const updateChecklist = (index: number, checked: boolean) => {
    setManualWorkflowDialog(prev => {
      const newChecklist = [...prev.checklist];
      newChecklist[index] = checked;
      return { ...prev, checklist: newChecklist };
    });
  };

  const allChecklistComplete = manualWorkflowDialog.checklist.every(Boolean);

  const getStatusBadge = (voucher: RefundVoucher) => {
    switch (voucher.status) {
      case 'pending_refund':
        return (
          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
            <Clock className="h-3 w-3 mr-1" />
            Pending
          </Badge>
        );
      case 'refunding':
        return (
          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
            <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
            Processing
          </Badge>
        );
      case 'refunded':
        return (
          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            {voucher.refund_method === 'razorpay_auto' ? 'Auto-Refunded' : 'Refunded'}
          </Badge>
        );
      case 'refund_failed':
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 cursor-help">
                  <XCircle className="h-3 w-3 mr-1" />
                  Failed
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs">{voucher.refund_error || 'Unknown error'}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      default:
        return <Badge variant="outline">{voucher.status}</Badge>;
    }
  };

  const pendingCount = vouchers.filter(v => v.status === 'pending_refund').length;
  const failedCount = vouchers.filter(v => v.status === 'refund_failed').length;
  const refundedCount = vouchers.filter(v => v.status === 'refunded').length;
  const totalPendingAmount = vouchers
    .filter(v => v.status === 'pending_refund')
    .reduce((sum, v) => sum + (v.refund_amount || v.total_amount), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{pendingCount}</p>
                <p className="text-sm text-muted-foreground">Pending Refunds</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{failedCount}</p>
                <p className="text-sm text-muted-foreground">Failed (Need Action)</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{refundedCount}</p>
                <p className="text-sm text-muted-foreground">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <DollarSign className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {currencySymbol}{totalPendingAmount.toFixed(2)}
                </p>
                <p className="text-sm text-muted-foreground">Total Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions */}
      {pendingCount > 0 && (
        <div className="flex flex-wrap gap-3">
          <Button
            onClick={processAllPending}
            disabled={processingAll}
            className="gap-2"
          >
            {processingAll ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <CreditCard className="h-4 w-4" />
            )}
            Process All via Razorpay
          </Button>
          <Button variant="outline" onClick={fetchVouchers} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>
      )}

      {/* Info Banner */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="py-4">
          <div className="flex gap-3">
            <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">How Refunds Work</p>
              <ul className="list-disc list-inside space-y-1 text-blue-700">
                <li><strong>Participants:</strong> Receive full refund to original payment method</li>
                <li><strong>Organizer:</strong> Cancellation fee is deducted from their refund</li>
                <li><strong>Auto-Refund:</strong> If Razorpay payment ID exists, refund is processed automatically</li>
                <li><strong>Manual:</strong> For payments without Razorpay ID, mark as complete after processing in your gateway</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Refund Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RotateCcw className="h-5 w-5" />
            Refund Queue
          </CardTitle>
          <CardDescription>
            Manage refunds for expired or cancelled orders
          </CardDescription>
        </CardHeader>
        <CardContent>
          {vouchers.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-emerald-500" />
              <p className="font-medium">No refunds to process</p>
              <p className="text-sm">All refunds have been completed or there are no expired orders.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Participant</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Payment ID</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {vouchers.map((voucher) => (
                    <TableRow key={voucher.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="font-medium">
                              {voucher.participant.name || 'Unknown'}
                              {voucher.is_organizer && (
                                <Badge variant="secondary" className="ml-2 text-xs">
                                  Organizer
                                </Badge>
                              )}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {voucher.participant.email}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="font-medium line-clamp-1">{voucher.request.product_name}</p>
                        <p className="text-xs text-muted-foreground">{voucher.voucher_number}</p>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-semibold text-emerald-600">
                            {currencySymbol}{(voucher.refund_amount || voucher.total_amount).toFixed(2)}
                          </p>
                          {voucher.is_organizer && voucher.refund_amount && voucher.refund_amount < voucher.total_amount && (
                            <p className="text-xs text-muted-foreground">
                              (After fee: {currencySymbol}{voucher.total_amount.toFixed(2)})
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {voucher.razorpay_payment_id ? (
                          <code className="text-xs bg-muted px-2 py-1 rounded">
                            {voucher.razorpay_payment_id.slice(0, 12)}...
                          </code>
                        ) : (
                          <span className="text-muted-foreground text-sm">—</span>
                        )}
                      </TableCell>
                      <TableCell>{getStatusBadge(voucher)}</TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          {voucher.status === 'pending_refund' && (
                            <>
                              {voucher.razorpay_payment_id ? (
                                <Button
                                  size="sm"
                                  variant="default"
                                  disabled={processing === voucher.id}
                                  onClick={() => setConfirmDialog({
                                    open: true,
                                    voucher,
                                    action: 'process',
                                  })}
                                  className="gap-1"
                                >
                                  {processing === voucher.id ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <ArrowRight className="h-3 w-3" />
                                  )}
                                  Process
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  disabled={processing === voucher.id}
                                  onClick={() => openManualWorkflow(voucher)}
                                  className="gap-1"
                                >
                                  <HandCoins className="h-3 w-3" />
                                  Guide Me
                                </Button>
                              )}
                            </>
                          )}
                          {voucher.status === 'refund_failed' && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={processing === voucher.id}
                                onClick={() => setConfirmDialog({
                                  open: true,
                                  voucher,
                                  action: 'retry',
                                })}
                                className="gap-1"
                              >
                                <RefreshCw className="h-3 w-3" />
                                Retry
                              </Button>
                              <Button
                                size="sm"
                                variant="secondary"
                                disabled={processing === voucher.id}
                                onClick={() => openManualWorkflow(voucher)}
                                className="gap-1"
                              >
                                <HandCoins className="h-3 w-3" />
                                Guide Me
                              </Button>
                            </>
                          )}
                          {voucher.status === 'refunded' && voucher.refund_completed_at && (
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {new Date(voucher.refund_completed_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onOpenChange={(open) => !open && setConfirmDialog({ open: false, action: 'process' })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {confirmDialog.action === 'process' && 'Process Razorpay Refund'}
              {confirmDialog.action === 'manual' && 'Mark Refund as Complete'}
              {confirmDialog.action === 'retry' && 'Retry Refund'}
            </DialogTitle>
            <DialogDescription>
              {confirmDialog.action === 'process' && (
                <>
                  This will initiate an automatic refund of{' '}
                  <strong>
                    {currencySymbol}
                    {(confirmDialog.voucher?.refund_amount || confirmDialog.voucher?.total_amount || 0).toFixed(2)}
                  </strong>{' '}
                  to <strong>{confirmDialog.voucher?.participant.email}</strong> via Razorpay.
                </>
              )}
              {confirmDialog.action === 'manual' && (
                <>
                  Please ensure you have already processed this refund in your payment gateway.
                  This will mark the refund as complete and notify the participant.
                </>
              )}
              {confirmDialog.action === 'retry' && (
                <>
                  This will retry the automatic refund. Previous error:{' '}
                  <span className="text-red-600">{confirmDialog.voucher?.refund_error}</span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmDialog({ open: false, action: 'process' })}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (confirmDialog.voucher) {
                  const action = confirmDialog.action === 'manual' ? 'mark_manual' : 'process_single';
                  processRefund(confirmDialog.voucher.id, action);
                }
              }}
              disabled={processing === confirmDialog.voucher?.id}
            >
              {processing === confirmDialog.voucher?.id ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Refund Guided Workflow Dialog */}
      <Dialog 
        open={manualWorkflowDialog.open} 
        onOpenChange={(open) => !open && setManualWorkflowDialog({ open: false, step: 1, checklist: [false, false, false, false], notes: "", feeAcknowledged: false })}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5 text-primary" />
              Manual Refund Workflow
            </DialogTitle>
            <DialogDescription>
              Follow these steps to process a manual refund for payments made outside Razorpay.
            </DialogDescription>
          </DialogHeader>

          {manualWorkflowDialog.voucher && (
            <div className="space-y-4">
              {/* Refund Details Card */}
              <Card className="bg-muted/50">
                <CardContent className="pt-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Recipient</span>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{manualWorkflowDialog.voucher.participant.name || 'Unknown'}</span>
                      {manualWorkflowDialog.voucher.is_organizer && (
                        <Badge variant="secondary" className="text-xs">Organizer</Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Email</span>
                    <div className="flex items-center gap-1">
                      <span className="font-medium text-sm">{manualWorkflowDialog.voucher.participant.email}</span>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-6 w-6"
                        onClick={() => copyToClipboard(manualWorkflowDialog.voucher!.participant.email, "Email")}
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Refund Amount</span>
                    <span className="font-bold text-lg text-emerald-600">
                      {currencySymbol}{(manualWorkflowDialog.voucher.refund_amount || manualWorkflowDialog.voucher.total_amount).toFixed(2)}
                    </span>
                  </div>
                  {manualWorkflowDialog.voucher.is_organizer && 
                   manualWorkflowDialog.voucher.refund_amount && 
                   manualWorkflowDialog.voucher.refund_amount < manualWorkflowDialog.voucher.total_amount && (
                    <div className="text-sm text-amber-700 bg-amber-50 dark:bg-amber-950/30 dark:text-amber-300 p-3 rounded-lg border border-amber-200 dark:border-amber-800">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium">Cancellation Fee Applied</p>
                          <p className="mt-1">
                            Original payment: {currencySymbol}{manualWorkflowDialog.voucher.total_amount.toFixed(2)}<br/>
                            Cancellation fee: -{currencySymbol}{(manualWorkflowDialog.voucher.total_amount - manualWorkflowDialog.voucher.refund_amount).toFixed(2)}<br/>
                            <span className="font-semibold">Amount to refund: {currencySymbol}{manualWorkflowDialog.voucher.refund_amount.toFixed(2)}</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Checklist */}
              <div className="space-y-3">
                <h4 className="font-medium text-sm">Complete these steps:</h4>
                
                <div className="space-y-2">
                  <div className="flex items-start gap-3 p-3 rounded-lg border bg-background">
                    <Checkbox 
                      id="step1" 
                      checked={manualWorkflowDialog.checklist[0]}
                      onCheckedChange={(checked) => updateChecklist(0, checked === true)}
                    />
                    <div className="flex-1">
                      <Label htmlFor="step1" className="font-medium cursor-pointer">
                        1. Log into Shopify Admin
                      </Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        Go to Orders → Find the cancelled order → Process refund
                      </p>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="gap-1"
                      onClick={() => window.open('https://admin.shopify.com', '_blank')}
                    >
                      <ExternalLink className="h-3 w-3" />
                      Open
                    </Button>
                  </div>

                  <div className="flex items-start gap-3 p-3 rounded-lg border bg-background">
                    <Checkbox 
                      id="step2" 
                      checked={manualWorkflowDialog.checklist[1]}
                      onCheckedChange={(checked) => updateChecklist(1, checked === true)}
                    />
                    <div className="flex-1">
                      <Label htmlFor="step2" className="font-medium cursor-pointer">
                        2. Initiate refund in Shopify
                      </Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        Refund exactly {currencySymbol}{(manualWorkflowDialog.voucher.refund_amount || manualWorkflowDialog.voucher.total_amount).toFixed(2)} to the customer
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 rounded-lg border bg-background">
                    <Checkbox 
                      id="step3" 
                      checked={manualWorkflowDialog.checklist[2]}
                      onCheckedChange={(checked) => updateChecklist(2, checked === true)}
                    />
                    <div className="flex-1">
                      <Label htmlFor="step3" className="font-medium cursor-pointer">
                        3. Wait for confirmation
                      </Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        Ensure Shopify shows "Refund processed" or similar confirmation
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 rounded-lg border bg-background">
                    <Checkbox 
                      id="step4" 
                      checked={manualWorkflowDialog.checklist[3]}
                      onCheckedChange={(checked) => updateChecklist(3, checked === true)}
                    />
                    <div className="flex-1">
                      <Label htmlFor="step4" className="font-medium cursor-pointer">
                        4. Notify participant (optional)
                      </Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        We'll send an automatic email, but you can also contact them directly
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Fee Acknowledgment - Required for organizers with fee deduction */}
              {manualWorkflowDialog.voucher.is_organizer && 
               manualWorkflowDialog.voucher.refund_amount && 
               manualWorkflowDialog.voucher.refund_amount < manualWorkflowDialog.voucher.total_amount && (
                <div className="p-4 rounded-lg border-2 border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700">
                  <div className="flex items-start gap-3">
                    <Checkbox 
                      id="fee-acknowledgment" 
                      checked={manualWorkflowDialog.feeAcknowledged}
                      onCheckedChange={(checked) => setManualWorkflowDialog(prev => ({ ...prev, feeAcknowledged: checked === true }))}
                      className="mt-0.5"
                    />
                    <div className="flex-1">
                      <Label htmlFor="fee-acknowledgment" className="font-medium cursor-pointer text-amber-800 dark:text-amber-200">
                        I confirm the cancellation fee has been retained
                      </Label>
                      <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                        I have refunded only <span className="font-bold">{currencySymbol}{manualWorkflowDialog.voucher.refund_amount.toFixed(2)}</span> (not the full {currencySymbol}{manualWorkflowDialog.voucher.total_amount.toFixed(2)}) and retained the {currencySymbol}{(manualWorkflowDialog.voucher.total_amount - manualWorkflowDialog.voucher.refund_amount).toFixed(2)} cancellation fee.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="refund-notes">Notes (optional)</Label>
                <Textarea
                  id="refund-notes"
                  placeholder="Add any notes about this refund (e.g., Shopify refund ID)..."
                  value={manualWorkflowDialog.notes}
                  onChange={(e) => setManualWorkflowDialog(prev => ({ ...prev, notes: e.target.value }))}
                  className="h-20"
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setManualWorkflowDialog({ open: false, step: 1, checklist: [false, false, false, false], notes: "", feeAcknowledged: false })}
            >
              Cancel
            </Button>
            <Button
              onClick={handleManualWorkflowComplete}
              disabled={
                !allChecklistComplete || 
                processing === manualWorkflowDialog.voucher?.id ||
                // Require fee acknowledgment for organizers with fee deduction
                (manualWorkflowDialog.voucher?.is_organizer && 
                 manualWorkflowDialog.voucher?.refund_amount && 
                 manualWorkflowDialog.voucher?.refund_amount < manualWorkflowDialog.voucher?.total_amount &&
                 !manualWorkflowDialog.feeAcknowledged)
              }
              className="gap-2"
            >
              {processing === manualWorkflowDialog.voucher?.id ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <CheckCircle2 className="h-4 w-4" />
              )}
              Mark Refund Complete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
